<template>
    <!-- Barre de navigation / menu en bas -->
    <nav :class="['fixed bottom-0 inset-x-0 bg-white px-4 py-1 flex items-center justify-around z-50 shadow-lg !border-t !border-gray-200 transition-all duration-10', { 'translate-y-full opacity-0 pointer-events-none': isKeyboardOpen }]">
      <!-- Bouton Accueil -->
      <router-link to="/home" class="flex flex-col items-center text-gray-600">
        <i class="inline-block h-10 w-10 mask mask-home bg-secondary"></i>
        <span class="text-md text-secondary">Accueil</span>
      </router-link>

      <!-- Grand cercle blanc derrière le bouton Scan -->
      <span class="relative flex items-center justify-center w-24 h-24 bg-white rounded-full -mt-14 shadow-lg">
        <!-- Bouton bleu “Scan” plus petit au centre -->
        <router-link
          :to="{ name: 'otag' }"
          class="flex flex-col items-center justify-center bg-primary w-20 h-20 rounded-full shadow-lg"
        >
          <i class="inline-block h-14 w-14 mask mask-otag bg-white -mb-2"></i>
          <span class="text-md text-white leading-none">Scan</span>
        </router-link>
      </span>

      <!-- Bouton Support -->
      <router-link :to="{name: 'support'}" class="flex flex-col items-center text-gray-600" id="btnBottomSupport">
        <i class="inline-block h-10 w-10 mask mask-question bg-secondary"></i>
        <span class="text-md text-secondary">Support</span>
      </router-link>
    </nav>
  </template>

  <script>
  export default {
    name: "AppMenu",
    data() {
      return {
        isKeyboardOpen: false,
        initialViewportHeight: 0,
        checkKeyboardTimer: null,
      };
    },
    mounted() {
      // Enregistrer la hauteur initiale
      this.initialViewportHeight = window.innerHeight;
      
      // Utiliser Visual Viewport API si disponible (meilleur pour iOS)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleViewportResize);
        window.visualViewport.addEventListener('scroll', this.handleViewportResize);
      } else {
        // Fallback pour les navigateurs plus anciens
        window.addEventListener('resize', this.handleWindowResize);
      }
      
      // Détecter le focus sur les inputs, textarea, select
      document.addEventListener('focusin', this.handleFocusIn, true);
      document.addEventListener('focusout', this.handleFocusOut, true);
      
      // Pour iOS, aussi écouter les événements de touche
      document.addEventListener('touchstart', this.handleTouchStart, true);
    },
    beforeDestroy() {
      // Nettoyer les event listeners
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleViewportResize);
        window.visualViewport.removeEventListener('scroll', this.handleViewportResize);
      } else {
        window.removeEventListener('resize', this.handleWindowResize);
      }
      
      document.removeEventListener('focusin', this.handleFocusIn, true);
      document.removeEventListener('focusout', this.handleFocusOut, true);
      document.removeEventListener('touchstart', this.handleTouchStart, true);
      
      if (this.checkKeyboardTimer) {
        clearTimeout(this.checkKeyboardTimer);
      }
    },
    methods: {
      checkIfInputFocused() {
        const activeElement = document.activeElement;
        if (!activeElement) return false;
        
        const tagName = activeElement.tagName;
        const isInput = tagName === 'INPUT' && 
          activeElement.type !== 'button' && 
          activeElement.type !== 'submit' && 
          activeElement.type !== 'reset' &&
          activeElement.type !== 'checkbox' &&
          activeElement.type !== 'radio';
        const isTextarea = tagName === 'TEXTAREA';
        const isSelect = tagName === 'SELECT';
        const isContentEditable = activeElement.contentEditable === 'true';
        
        return isInput || isTextarea || isSelect || isContentEditable;
      },
      handleViewportResize() {
        // Sur iOS, le visualViewport.height diminue quand le clavier s'ouvre
        if (window.visualViewport) {
          const heightDifference = this.initialViewportHeight - window.visualViewport.height;
          const isInputFocused = this.checkIfInputFocused();
          this.isKeyboardOpen = heightDifference > 100 || isInputFocused;
        }
      },
      handleWindowResize() {
        // Fallback pour les navigateurs sans Visual Viewport API
        const heightDifference = this.initialViewportHeight - window.innerHeight;
        const isInputFocused = this.checkIfInputFocused();
        this.isKeyboardOpen = heightDifference > 100 || isInputFocused;
      },
      handleFocusIn(event) {
        const target = event.target;
        const tagName = target.tagName;
        const isInput = tagName === 'INPUT' && 
          target.type !== 'button' && 
          target.type !== 'submit' && 
          target.type !== 'reset' &&
          target.type !== 'checkbox' &&
          target.type !== 'radio';
        const isTextarea = tagName === 'TEXTAREA';
        const isSelect = tagName === 'SELECT';
        const isContentEditable = target.contentEditable === 'true';
        
        if (isInput || isTextarea || isSelect || isContentEditable) {
          // Délai pour laisser le clavier s'ouvrir sur iOS
          if (this.checkKeyboardTimer) {
            clearTimeout(this.checkKeyboardTimer);
          }
          this.checkKeyboardTimer = setTimeout(() => {
            this.isKeyboardOpen = true;
          }, 300);
        }
      },
      handleFocusOut(event) {
        const target = event.target;
        const tagName = target.tagName;
        const isInput = tagName === 'INPUT' && 
          target.type !== 'button' && 
          target.type !== 'submit' && 
          target.type !== 'reset' &&
          target.type !== 'checkbox' &&
          target.type !== 'radio';
        const isTextarea = tagName === 'TEXTAREA';
        const isSelect = tagName === 'SELECT';
        const isContentEditable = target.contentEditable === 'true';
        
        if (isInput || isTextarea || isSelect || isContentEditable) {
          // Délai pour vérifier si un autre input a le focus
          if (this.checkKeyboardTimer) {
            clearTimeout(this.checkKeyboardTimer);
          }
          this.checkKeyboardTimer = setTimeout(() => {
            const isInputFocused = this.checkIfInputFocused();
            if (!isInputFocused) {
              // Vérifier aussi la hauteur de la fenêtre
              if (window.visualViewport) {
                const heightDifference = this.initialViewportHeight - window.visualViewport.height;
                this.isKeyboardOpen = heightDifference > 100;
              } else {
                const heightDifference = this.initialViewportHeight - window.innerHeight;
                this.isKeyboardOpen = heightDifference > 100;
              }
            }
          }, 300);
        }
      },
      handleTouchStart(event) {
        // Sur iOS, détecter si on touche un input
        const target = event.target;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
          setTimeout(() => {
            if (document.activeElement === target) {
              this.isKeyboardOpen = true;
            }
          }, 300);
        }
      },
    },
  };
  </script>

  <style scoped>
  /* Ajustement des ombres et proportions mobiles */
  nav {
    -webkit-tap-highlight-color: transparent;
  }
  </style>



// WEBPACK FOOTER //
// BottomMenu.vue?797d038e